import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, updateDoc, getDocs, query, orderBy, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { PlusIcon, TrashIcon, BookmarkIcon } from '@heroicons/react/solid';
import { UserContext } from '../../providers/UserProvider';
import TemplateSelector from './TemplateSelector';
import { designsCollection, templatesCollection, getValueWithoutUnit, checkUser, COLORS, FUENTES } from '../../utils';
import NameCard from './NameCard';

const inputStyles =
    'placeholder:text-neutral-300 mt-1 p-2 px-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50';
const Admin = (props) => {
    const { title } = props;
    let params = useParams();
    let navigate = useNavigate();
    const user = useContext(UserContext);
    const isValidUser = checkUser(user);
    const [nStickers, setNStickers] = useState(1);
    const [image, setImage] = useState('');
    const [thumbnail, setThumbnail] = useState('');
    const [category, setCategory] = useState('');
    const [productUrl, setProductUrl] = useState('');
    const [designName, setDesignName] = useState('');
    const [defaultColor, setDefaultColor] = useState('FFFFFF');
    const [colorList, setColorList] = useState(COLORS);
    const [fontList, setFontList] = useState(FUENTES);
    const [defaultTypography, setDefaultTypography] = useState('Acumin');
    const [defaultAlignment, setDefaultAlignment] = useState('center');
    const [hideColorSelector, setHideColorSelector] = useState(false);
    const [hideFontSelector, setHideFontSelector] = useState(false);
    const [iniciales, setIniciales] = useState(false);
    const [selectColorList, setSelectColorList] = useState(false);
    const [selectFontList, setSelectFontList] = useState(false);
    const [designSaved, setDesignSaved] = useState(false);
    const [showNotice, setShowNotice] = useState(false);
    const [designId, setDesignId] = useState(null);
    const [apply, setApply] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [dbTemplates, setDbTemplates] = useState([]);
    const [templateName, setTemplateName] = useState('');
    const [maxLength1, setMaxLength1] = useState(0);
    const [maxLength2, setMaxLength2] = useState(0);

    const saveButton = useRef();
    const prevCoords = useRef({});
    const coordStyles = useRef([]);
    const colorOptions = useRef(
        COLORS.map((color) => ({
            ...color,
            value: color.hex,
            label: color.name,
        })),
    );

    const fontOptions = useRef(
        FUENTES.map((fuente) => ({
            ...fuente,
            value: fuente.value,
            label: fuente.name,
        })),
    );

    const handleColorListSelection = (selected) => {
        const availableColorList = selected.map((color) => ({
            name: color.name,
            hex: color.hex,
        }));
        setColorList(availableColorList);
    };

    const handleFontListSelection = (selected) => {
        const availableFontList = selected.map((font) => ({
            name: font.name,
            value: font.value,
        }));
        setFontList(availableFontList);
    };

    const onChange = (evt) => {
        let position = evt.target.id.split('-');
        position = position[position.length - 1];

        switch (evt.target.name) {
            case 'productUrl':
                setProductUrl(evt.target.value);
                break;
            case 'name':
                setDesignName(evt.target.value);
                break;
            case 'color':
                setDefaultColor(evt.target.value);
                break;
            case 'typography':
                setDefaultTypography(evt.target.value);
                break;
            case 'alignment':
                setDefaultAlignment(evt.target.value);
                break;
            case 'maxlength1':
                setMaxLength1(evt.target.value);
                break;
            case 'maxlength2':
                setMaxLength2(evt.target.value);
                break;
            case 'hideColorSelector':
                setHideColorSelector(evt.target.checked);
                break;
            case 'hideFontSelector':
                setHideFontSelector(evt.target.checked);
                break;
            case 'iniciales':
                setIniciales(evt.target.checked);
                break;
            case 'selectColorList':
                setSelectColorList(evt.target.checked);
                break;
            case 'selectFontList':
                setSelectFontList(evt.target.checked);
                break;
            case 'designImage':
                setImage(evt.target.value);
                break;
            case 'designThumbnail':
                setThumbnail(evt.target.value);
                break;
            case 'category':
                setCategory(evt.target.value);
                break;
            case 'label-width':
                coordStyles.current[position] = {
                    ...coordStyles.current[position],
                    position: Number(position),
                    width: `${evt.target.value}px`,
                };
                prevCoords.current.width = evt.target.value;
                setApply((a) => !a);
                break;
            case 'font-size':
                coordStyles.current[position] = {
                    ...coordStyles.current[position],
                    position: Number(position),
                    fontSize: `${evt.target.value}px`,
                };
                prevCoords.current.fontSize = evt.target.value;
                setApply((a) => !a);
                break;
            case 'coord-top':
                coordStyles.current[position] = {
                    ...coordStyles.current[position],
                    position: Number(position),
                    top: `${evt.target.value}px`,
                };
                prevCoords.current.top = evt.target.value;
                setApply((a) => !a);
                break;
            case 'coord-left':
                coordStyles.current[position] = {
                    ...coordStyles.current[position],
                    position: Number(position),
                    left: `${evt.target.value}px`,
                };
                prevCoords.current.left = evt.target.value;
                setApply((a) => !a);
                break;

            default:
                return false;
        }
    };

    const removeLastSticker = (evt) => {
        var ev2 = new Event('input', { bubbles: true });
        ev2.simulated = true;
        // document.querySelector('form:last-child input').value = 'With simulated flag';
        document.getElementById('label-width-0').dispatchEvent(ev2);

        coordStyles.current.splice(nStickers - 1, 1);
        setNStickers(Number(nStickers - 1));
    };

    const removeAllStickers = (evt) => {
        setNStickers(Number(0));
        coordStyles.current = [];
    };

    const addSticker = (evt) => {
        setNStickers(Number(nStickers + 1));
    };

    const saveDesign = async (e) => {
        e.preventDefault();
        if (!thumbnail || !image || !productUrl || !designName) {
            alert(
                'Por favor completa los campos obligatorios: Imagen miniatura, URL de la imagen, Producto en WordPress y Nombre del diseño.',
            );
            return;
        }
        if (saveButton.current) saveButton.current.setAttribute('disabled', 'disabled');
        if (!designSaved && !params.designId) {
            const newDesign = {
                name: designName,
                labels: coordStyles.current,
                image,
                product: Number(productUrl),
                category,
                thumbnail,
                defaultColor,
                defaultTypography,
                defaultAlignment,
                maxLength1,
                maxLength2,
                hideColorSelector,
                hideFontSelector,
                iniciales,
                active: true,
                createdAt: new Date(),
            };

            if (!maxLength1) {
                newDesign.maxLength1 = 100;
            }

            if (!maxLength2) {
                newDesign.maxLength2 = 100;
            }

            if (selectColorList && colorList.length > 0) {
                newDesign.availableColorList = [...colorList];
            }

            if (selectFontList && fontList.length > 0) {
                newDesign.availableFontList = [...fontList];
            }

            const designAdded = await addDoc(collection(db, designsCollection), newDesign);
            console.info('Design saved as: ', designAdded.id, newDesign);
            setShowNotice({ type: 'update', value: true });
            setTimeout(() => {
                setShowNotice({ type: 'update', value: false });
            }, 3000);
            saveButton.current.removeAttribute('disabled');
            navigate(`/admin/${designAdded.id}`);
        } else {
            console.log('Editing...', designId);
            updateDesign();
        }
    };

    const updateDesign = async () => {
        if (!designId) return;
        setShowNotice({ type: 'update', value: false });
        const updatedDesign = {
            name: designName,
            labels: coordStyles.current,
            image,
            product: Number(productUrl),
            category,
            thumbnail,
            defaultColor,
            defaultTypography,
            defaultAlignment,
            maxLength1,
            maxLength2,
            hideColorSelector,
            hideFontSelector,
            iniciales,
        };

        if (selectColorList && colorList.length > 0) {
            updatedDesign.availableColorList = [...colorList];
        }

        if (selectFontList && fontList.length > 0) {
            updatedDesign.availableFontList = [...fontList];
        }

        const q = doc(db, designsCollection, designId);
        const designSnap = await getDoc(q);
        if (designSnap.exists()) {
            if (!designSnap.data().createdAt) {
                updatedDesign.createdAt = new Date();
            }
            const designRef = doc(db, designsCollection, designId);
            await updateDoc(designRef, updatedDesign);
        }

        setDesignSaved(true);
        setShowNotice({ type: 'update', value: true });
        setTimeout(() => {
            setShowNotice({ type: 'update', value: false });
        }, 3000);
        console.log('Design edited.');
        saveButton.current.removeAttribute('disabled');
    };

    const populateDesign = useCallback(async () => {
        console.log('Populating design');
        setShowNotice({ type: 'loading', value: true });
        const q = doc(db, designsCollection, params.designId);
        const designSnap = await getDoc(q);

        if (designSnap.exists()) {
            const nStickers = designSnap.data().labels.length;
            setDesignId(params.designId);
            setImage(designSnap.data().image);
            setThumbnail(designSnap.data().thumbnail);
            setDesignName(designSnap.data().name);
            setDefaultColor(designSnap.data().defaultColor);
            setDefaultTypography(designSnap.data().defaultTypography);
            setDefaultAlignment(designSnap.data().defaultAlignment);
            setHideColorSelector(designSnap.data().hideColorSelector);
            setHideFontSelector(designSnap.data().hideFontSelector);
            setIniciales(designSnap.data().iniciales ?? false);
            setSelectColorList(designSnap.data().availableColorList?.length);
            setColorList(designSnap.data().availableColorList ?? []);
            setSelectFontList(designSnap.data().availableFontList?.length);
            setFontList(designSnap.data().availableFontList ?? []);
            setProductUrl(designSnap.data().product);
            setCategory(designSnap.data().category);
            setNStickers(nStickers);
            setMaxLength1(designSnap.data().maxLength1);
            setMaxLength2(designSnap.data().maxLength2);

            designSnap.data().labels.forEach((sticker, i) => {
                coordStyles.current.push({
                    top: sticker.top,
                    left: sticker.left,
                    width: sticker.width,
                    fontSize: sticker.fontSize,
                    position: sticker.position,
                });
            });
        } else {
            console.log('Design not found!');
            setShowNotice({ type: 'loading', value: false });
            navigate(`/admin`);
        }
        setTimeout(() => {
            setShowNotice({ type: 'loading', value: false });
        }, 2000);
    }, [params, navigate]);

    const applyTemplate = (templateCoords) => {
        if (!templateCoords) return;
        setNStickers(templateCoords.length);
        templateCoords.forEach((coord) => {
            coordStyles.current.push(coord);
        });
    };

    const fetchTemplates = useCallback(async () => {
        const q = query(collection(db, templatesCollection), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        setDbTemplates(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, []);

    const saveTemplate = async () => {
        if (!templateName.trim()) return;
        document.getElementById('save-template-dialog').close();
        setShowNotice({ type: 'loading', value: true, message: 'Guardando plantilla...' });
        const coords = coordStyles.current.slice(0, nStickers).filter(Boolean);
        await addDoc(collection(db, templatesCollection), {
            name: templateName.trim(),
            category: 'custom',
            createdAt: new Date(),
            coords,
        });
        setTemplateName('');
        await fetchTemplates();
        setShowNotice({ type: 'loading', value: false });
    };

    const deleteTemplate = async (id) => {
        setShowNotice({ type: 'loading', value: true, message: 'Eliminando plantilla...' });
        await deleteDoc(doc(db, templatesCollection, id));
        await fetchTemplates();
        setShowNotice({ type: 'loading', value: false });
    };

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    useEffect(() => {
        if (!params.designId) return;
        setDesignId(params.designId);
        populateDesign();
    }, [params.designId, populateDesign]);

    const isScrolledIntoView = (el) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const elemTop = rect.top;
        const elemBottom = rect.bottom;

        // const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
        // Partially visible elements return true:
        const isVisible = elemTop < window.innerHeight && elemBottom >= 0;
        return isVisible;
    };
    useEffect(() => {
        document.title = title;
        const handleScroll = () => {
            const elem = document.querySelectorAll('.inView')[0];
            setScrolled(!isScrolledIntoView(elem));
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [title]);

    const handleDeletion = () => {
        if (window.confirm('¿Está seguro?')) {
            removeAllStickers();
        }
    };

    return (
        <>
            {isValidUser ? (
                <>
                    <div className="relative">
                        {/* Header */}
                        <nav className="bg-gray-800 sticky top-0 shadow-lg z-50">
                            <div className="mx-auto  container px-4 sm:px-6 lg:px-8">
                                <div className="flex h-16 items-center justify-between">
                                    <div className="flex items-center">
                                        <div className="shrink-0">
                                            <img src="/favicon.png" alt="Stickapp" className="size-8 inline" />{' '}
                                            <h1 className="inline text-white font-medium text-xl ml-2">
                                                Stickapp <sup>v3</sup>
                                            </h1>
                                        </div>
                                        <div className="hidden md:block">
                                            <div className="ml-10 flex items-baseline space-x-4">
                                                <Link
                                                    to={'/admin/list'}
                                                    className="rounded-md px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                                    Listado de diseños
                                                </Link>
                                                <a
                                                    href={'/admin/create'}
                                                    className="rounded-md bg-gray-900 px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                                    Nuevo diseño
                                                </a>
                                                {designsCollection === 'designs_dev' && (
                                                    <Link
                                                        to={'/admin/tools/backfill-created-at'}
                                                        className="rounded-md px-3 py-2 uppercase text-xs font-medium text-gray-300 hover:bg-white/5 hover:text-white">
                                                        Admin
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hidden md:block">
                                        <div className="ml-4 flex items-center md:ml-6">
                                            {designsCollection === 'designs_dev' && (
                                                <span className="text-xs uppercase right-1/2 top-0 text-gray-500">
                                                    {designsCollection}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </nav>

                        <header className="relative bg-gray-100 shadow-sm">
                            <div className="mx-auto container px-4 py-4 sm:px-6 lg:px-12">
                                <h1 className="text-2xl font-semibold text-gray-700">
                                    {params.designId ? `Editando: ${designName}` : 'Nuevo Diseño'}
                                </h1>
                            </div>
                        </header>

                        {showNotice.type === 'loading' && showNotice.value === true && (
                            <div className="fixed inset-0 z-100 flex items-center justify-center bg-white/40 backdrop-blur-md">
                                <p className="text-2xl font-semibold text-gray-600 tracking-wide">
                                    {showNotice.message ?? 'Cargando el diseño...'}
                                </p>
                            </div>
                        )}

                        <div
                            className={`relative p-4 bg-green-300 ${
                                showNotice.value === true && showNotice.type !== 'loading'
                                    ? 'opacity-90'
                                    : 'hidden opacity-0'
                            } w-full transition-opacity duration-500 ease-in-out shadow-md z-30`}>
                            <p className="text-green-800 text-center">Diseño guardado correctamente</p>
                        </div>

                        {/* Design form */}
                        <section className="bg-gray-200 py-8">
                            <div className="container mx-auto grid grid-cols-5 gap-6">
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Ruta de la miniatura del diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        name="designThumbnail"
                                        className={inputStyles}
                                        value={thumbnail}
                                        placeholder="Imagen miniatura del diseño"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Ruta de imagen para este diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        name="designImage"
                                        className={inputStyles}
                                        value={image}
                                        placeholder="URL de la imagen"
                                        required
                                    />
                                </div>
                                <div className="relative w-full mb-4 inView">
                                    <a
                                        className="absolute -top-0 right-2 text-xs hover:text-blue-500 hover:underline"
                                        href="https://cln.sh/D0jbS3"
                                        target="_blank"
                                        rel="noreferrer"
                                        title="¿Cómo encontrarlo?">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth={1.5}
                                            stroke="currentColor"
                                            className="w-6 h-6">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                                            />
                                        </svg>
                                    </a>
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Producto en WordPress:
                                    </label>
                                    <input
                                        type="number"
                                        onChange={onChange}
                                        id={`productUrl-0`}
                                        name={`productUrl`}
                                        value={productUrl}
                                        className={inputStyles}
                                        placeholder="Código numérico del producto, ej: 200019"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Nombre para el diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        id={`name-0`}
                                        name={`name`}
                                        value={designName}
                                        className={inputStyles}
                                        placeholder="Por ejemplo Piratas, Fútbol, Estrella, etc"
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="text-sm text-gray-600 font-semibold mb-2">
                                        Categoría para el diseño:
                                    </label>
                                    <input
                                        type="text"
                                        onChange={onChange}
                                        id={`category-0`}
                                        name={`category`}
                                        value={category}
                                        className={inputStyles}
                                        placeholder="Por ejemplo: Niño, Niña, Teen Niño, etc"
                                    />
                                </div>
                            </div>
                        </section>

                        {/* Painting area */}
                        <section className="container mx-auto">
                            <div className="lg:grid grid-cols-2 gap-4">
                                <div className="relative mb-6 max-w-2xl bg-neutral-100 mt-8">
                                    <div
                                        className={`fixed ${
                                            scrolled ? 'top-10' : ''
                                        } rounded-lg overflow-hidden mx-12 my-8 shadow-lg`}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const cardId = Number(e.dataTransfer.getData('cardId'));
                                            const offsetX = Number(e.dataTransfer.getData('offsetX'));
                                            const offsetY = Number(e.dataTransfer.getData('offsetY'));
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const newLeft = Math.round(e.clientX - rect.left - offsetX);
                                            const newTop = Math.round(e.clientY - rect.top - offsetY);
                                            coordStyles.current[cardId] = {
                                                ...coordStyles.current[cardId],
                                                position: cardId,
                                                left: `${newLeft}px`,
                                                top: `${newTop}px`,
                                            };
                                            setApply((a) => !a);
                                        }}>
                                        {nStickers &&
                                            [...Array(nStickers)].map((e, i) => {
                                                const extraStyling = {};
                                                if (coordStyles.current[i]) {
                                                    const fontSize = coordStyles.current[i].fontSize?.split('px') || 14;
                                                    extraStyling.lh = fontSize[0] < 16 ? 'et-lh-13' : '';
                                                }
                                                return (
                                                    <NameCard
                                                        key={`name-card-${i}`}
                                                        id={i}
                                                        name={iniciales ? 'ABC' : 'Nombre Apellido'}
                                                        coords={coordStyles.current[i]}
                                                        defaults={{
                                                            color: defaultColor,
                                                            typography: defaultTypography,
                                                            alignment: defaultAlignment,
                                                        }}
                                                    />
                                                );
                                            })}
                                        {image && (
                                            <>
                                                <img
                                                    className="z-0"
                                                    width="565"
                                                    height="565"
                                                    src={image}
                                                    alt="Previsualiza"
                                                />
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="relative bg-white z-10 p-8 pt-0 pb-20 rounded-xl my-4 shadow-md">
                                    {/* Opciones */}
                                    <div>
                                        <h4 className="text-2xl font-medium text-gray-600 mt-8 mb-4">Opciones:</h4>
                                        <div>
                                            <label className="block text text-gray-600 block my-4 mt-6">
                                                Color del texto por defecto:
                                            </label>
                                            {COLORS.map((color) => {
                                                const { hex } = color;
                                                return (
                                                    <button
                                                        className={`cursor-pointer w-8 h-8 m-0.5 text-black rounded-full border-2 hover:border-blue-500 palette_${hex} ${
                                                            defaultColor === hex
                                                                ? 'border-etiquette'
                                                                : 'border-gray-200'
                                                        }`}
                                                        key={hex}
                                                        onClick={onChange}
                                                        name="color"
                                                        value={hex}></button>
                                                );
                                            })}
                                            <div className={`mt-2 ${hideColorSelector ? 'mb-8' : 'mb-4'}`}>
                                                <input
                                                    id="hideColorSelector"
                                                    type="checkbox"
                                                    name="hideColorSelector"
                                                    className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-hidden cursor-pointer"
                                                    onChange={onChange}
                                                    checked={!!hideColorSelector}
                                                />
                                                <label
                                                    htmlFor="hideColorSelector"
                                                    className="inline-block text-sm text-gray-600 font-semibold">
                                                    ¿Deshabilitar selección de color al cliente?
                                                </label>
                                            </div>
                                            {!hideColorSelector && (
                                                <>
                                                    <div className={`mt-2 ${!selectColorList ? 'mb-8' : 'mb-4'}`}>
                                                        <input
                                                            id="selectColorList"
                                                            type="checkbox"
                                                            name="selectColorList"
                                                            className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-hidden cursor-pointer"
                                                            onChange={onChange}
                                                            checked={!!selectColorList}
                                                        />
                                                        <label
                                                            htmlFor="selectColorList"
                                                            className="inline-block text-sm text-gray-600 font-semibold">
                                                            ¿Limitar colores disponibles?
                                                        </label>
                                                    </div>

                                                    {selectColorList && (
                                                        <>
                                                            {params.designId && (
                                                                <>
                                                                    <h4 className="text-sm text-gray-500">
                                                                        Colores seleccionados actualmente:
                                                                    </h4>
                                                                    <ul className="mt-2 mb-4">
                                                                        {colorList.map((color) => {
                                                                            return (
                                                                                <li
                                                                                    key={color.hex}
                                                                                    className={`text-sm font-medium bg-gray-200 rounded p-1 px-2 inline m-1`}>
                                                                                    {color.name}
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                </>
                                                            )}

                                                            <Select
                                                                isMulti
                                                                id="colorList"
                                                                name="colorList"
                                                                options={colorOptions.current}
                                                                className="basic-multi-select mb-8"
                                                                classNamePrefix="select"
                                                                onChange={handleColorListSelection}
                                                                placeholder="Selecciona los colores disponibles para este diseño"
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <hr className="my-6 border-gray-300" />
                                        <div>
                                            <label className="block text-sm text-gray-600 font-semibold my-2">
                                                Tipografía por defecto:
                                            </label>
                                            <div className="grid grid-cols-5 gap-2">
                                                {FUENTES.map((fuente) => {
                                                    const { name, value } = fuente;
                                                    return (
                                                        <button
                                                            className={`cursor-pointer rounded-lg h-16 bg-gray-100 hover:bg-etiquette hover:text-white text-gray-500 uppercase text-xs border-2 py-2 font_${value} ${
                                                                defaultTypography === value
                                                                    ? 'border-etiquette'
                                                                    : 'border-gray-100'
                                                            } ${name === 'Sacramento' ? 'px-1' : 'px-3'}`}
                                                            key={value}
                                                            onClick={onChange}
                                                            name="typography"
                                                            value={value}>
                                                            {name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className={`mt-2 ${!selectColorList ? 'mb-8' : 'mb-4'}`}>
                                                <input
                                                    id="hideFontSelector"
                                                    type="checkbox"
                                                    name="hideFontSelector"
                                                    className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-hidden cursor-pointer"
                                                    onChange={onChange}
                                                    checked={!!hideFontSelector}
                                                />
                                                <label
                                                    htmlFor="hideFontSelector"
                                                    className="inline-block text-sm text-gray-600 font-semibold">
                                                    ¿Deshabilitar selección de tipografía al cliente?
                                                </label>
                                            </div>
                                            {!hideFontSelector && (
                                                <>
                                                    <div className={`mt-2 ${!selectFontList ? 'mb-8' : 'mb-4'}`}>
                                                        <input
                                                            id="selectFontList"
                                                            type="checkbox"
                                                            name="selectFontList"
                                                            className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-hidden"
                                                            onChange={onChange}
                                                            checked={!!selectFontList}
                                                        />
                                                        <label
                                                            htmlFor="selectFontList"
                                                            className="inline-block text-sm text-gray-600 font-semibold">
                                                            ¿Limitar tipografías disponibles?
                                                        </label>
                                                    </div>

                                                    {selectFontList && (
                                                        <>
                                                            {params.designId && (
                                                                <>
                                                                    <h4 className="text-sm text-gray-500">
                                                                        tipografías seleccionados actualmente:
                                                                    </h4>
                                                                    <ul className="mt-2 mb-4">
                                                                        {fontList.map((font) => {
                                                                            return (
                                                                                <li
                                                                                    key={font.value}
                                                                                    className={`text-sm font-medium bg-gray-200 rounded p-1 px-2 inline m-1`}>
                                                                                    {font.name}
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                </>
                                                            )}

                                                            <Select
                                                                isMulti
                                                                id="fontList"
                                                                name="fontList"
                                                                options={fontOptions.current}
                                                                className="basic-multi-select mb-8"
                                                                classNamePrefix="select"
                                                                onChange={handleFontListSelection}
                                                                placeholder="Selecciona las tipografías disponibles para este diseño"
                                                            />
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <hr className="my-6 border-gray-300" />
                                        <div className="mt-2 mb-4">
                                            <input
                                                id="iniciales"
                                                type="checkbox"
                                                name="iniciales"
                                                className="inline-block mr-2 w-4 h-4 p-2 border-2 border-gray-200 outline-hidden cursor-pointer"
                                                onChange={onChange}
                                                checked={!!iniciales}
                                            />
                                            <label
                                                htmlFor="iniciales"
                                                className="inline-block text-sm text-gray-600 font-semibold">
                                                ¿Diseño de Iniciales? (oculta apellido, muestra solo campo de iniciales)
                                            </label>
                                        </div>

                                        <div className="grid grid-cols-2">
                                            {/* Alineación textos */}
                                            <div>
                                                <label className="block text text-gray-600 block my-4 mt-6">
                                                    Alineación del texto por defecto:
                                                </label>

                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'left'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentleft"
                                                        type="radio"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="left"
                                                    />

                                                    <label htmlFor="alignmentleft" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'center'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentcenter"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="center"
                                                        type="radio"
                                                    />
                                                    <label htmlFor="alignmentcenter" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                                <div
                                                    className={`inline-block cursor-pointer m-1 text-black border-2 hover:border-blue-500 ${
                                                        defaultAlignment === 'right'
                                                            ? 'border-etiquette'
                                                            : 'border-gray-200'
                                                    }`}>
                                                    <input
                                                        id="alignmentright"
                                                        className={`hidden w-8 h-8`}
                                                        onClick={onChange}
                                                        name="alignment"
                                                        value="right"
                                                        type="radio"
                                                    />
                                                    <label htmlFor="alignmentright" className="cursor-pointer">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-6 h-6">
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25"
                                                            />
                                                        </svg>
                                                    </label>
                                                </div>
                                            </div>
                                            {/* Longitud de caracteres */}
                                            <div>
                                                <label className="block text text-gray-600 block my-4 mt-6">
                                                    Longitud máxima de caracteres
                                                </label>
                                                <div className="grid grid-cols-2 gap-6">
                                                    <div>
                                                        <h4 className="text-sm text-gray-500 font-semibold mb-2">
                                                            Primera línea:
                                                        </h4>
                                                        <input
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                                                            type="number"
                                                            name="maxlength1"
                                                            onChange={onChange}
                                                            value={maxLength1}
                                                        />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-sm text-gray-500 font-semibold mb-2">
                                                            Segunda línea:
                                                        </h4>
                                                        <input
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                                                            type="number"
                                                            name="maxlength2"
                                                            value={maxLength2}
                                                            onChange={onChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <hr className="my-6 border-gray-300" />

                                    <TemplateSelector
                                        applyTemplate={applyTemplate}
                                        dbTemplates={dbTemplates}
                                        onDeleteTemplate={deleteTemplate}
                                    />
                                    <hr className="mt-10 mb-6 border-gray-300" />

                                    {/* Botones */}
                                    <div className="z-20 w-auto flex flex-wrap gap-2 items-center right-12 mb-8">
                                        <button
                                            className="cursor-pointer transition duration-300 bg-green-500 hover:bg-green-700 text-white rounded-full py-2 px-2 shadow-md"
                                            onClick={addSticker}>
                                            <PlusIcon className="float-left h-5 w-5 text-white" />
                                            <span className="inline">Nuevo</span>
                                        </button>
                                        {nStickers > 1 && (
                                            <>
                                                <button
                                                    className="cursor-pointer transition duration-300 bg-red-500 hover:bg-red-700 text-white rounded-full py-2 px-4 shadow-md"
                                                    onClick={removeLastSticker}>
                                                    <TrashIcon className="float-left h-5 w-5 text-white" />
                                                    <span className="inline">Último</span>
                                                </button>
                                                <button
                                                    className="cursor-pointer transition duration-300 bg-red-500 hover:bg-red-700 text-white rounded-full py-2 px-4 shadow-md"
                                                    onClick={handleDeletion}>
                                                    <TrashIcon className="float-left h-5 w-5 text-white" />
                                                    <span className="inline">Todos</span>
                                                </button>
                                            </>
                                        )}

                                        <button
                                            command="show-modal"
                                            commandfor="save-template-dialog"
                                            type="button"
                                            className="ml-auto cursor-pointer transition duration-300 bg-orange-500 hover:bg-orange-700 text-white/80 font-medium rounded-full py-2 px-4 shadow-md">
                                            <BookmarkIcon className="float-left h-5 w-5 text-white" />
                                            <span className="inline">Guardar plantilla</span>
                                        </button>

                                        <el-dialog>
                                            <dialog
                                                id="save-template-dialog"
                                                aria-labelledby="dialog-title"
                                                className="fixed inset-0 size-auto max-h-none max-w-none overflow-y-auto bg-transparent backdrop:bg-transparent">
                                                <el-dialog-backdrop className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"></el-dialog-backdrop>

                                                <div
                                                    tabIndex="0"
                                                    className="flex min-h-full items-end justify-center p-4 text-center focus:outline-none sm:items-center sm:p-0">
                                                    <el-dialog-panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-sm sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95">
                                                        <div>
                                                            <h3 className="text-lg font-semibold text-gray-700 mb-4">
                                                                Nombre de la plantilla
                                                            </h3>
                                                            <input
                                                                type="text"
                                                                className={inputStyles}
                                                                placeholder="Ej. Sticker redondo pequeño"
                                                                value={templateName}
                                                                onChange={(e) => setTemplateName(e.target.value)}
                                                                required
                                                            />
                                                            <div className="flex justify-end gap-3 mt-6">
                                                                <button
                                                                    command="close"
                                                                    commandfor="save-template-dialog"
                                                                    type="button"
                                                                    className="px-5 py-2 rounded-full text-gray-500 hover:bg-gray-100 cursor-pointer"
                                                                    onClick={() => setTemplateName('')}>
                                                                    Cancelar
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="px-5 py-2 max-w-40 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold cursor-pointer"
                                                                    onClick={saveTemplate}>
                                                                    Guardar
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </el-dialog-panel>
                                                </div>
                                            </dialog>
                                        </el-dialog>
                                    </div>

                                    <hr className="mt-4 mb-8 border-gray-300" />

                                    {/* Coordenadas */}

                                    {nStickers &&
                                        [...Array(nStickers)].map((e, i) => {
                                            return (
                                                <div
                                                    key={`stickerCoord-${i}`}
                                                    className="relative lg:grid grid-cols-4 gap-3 mb-6 pb-4 px-14 border-b-2 border-gray-100">
                                                    <span className="hidden lg:block absolute text-4xl text-gray-300 font-bold left-0 top-10">
                                                        {i + 1}·
                                                    </span>
                                                    {/* Ancho */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Ancho:
                                                        </h4>
                                                        <input
                                                            type="number"
                                                            onChange={onChange}
                                                            id={`label-width-${i}`}
                                                            name={`label-width`}
                                                            value={
                                                                getValueWithoutUnit(
                                                                    coordStyles.current[i]?.width,
                                                                    'px',
                                                                ) ?? '150'
                                                            }
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                                                        />
                                                    </div>
                                                    {/* Fuente */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Fuente:
                                                        </h4>
                                                        <select
                                                            onChange={onChange}
                                                            id={`font-size-${i}`}
                                                            name={`font-size`}
                                                            value={
                                                                getValueWithoutUnit(
                                                                    coordStyles.current[i]?.fontSize,
                                                                    'px',
                                                                ) ?? '12'
                                                            }
                                                            className="mt-1 p-3 px-4 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50">
                                                            <option value="8">8</option>
                                                            <option value="10">10</option>
                                                            <option value="12">12</option>
                                                            <option value="14">14</option>
                                                            <option value="16">16</option>
                                                            <option value="18">18</option>
                                                            <option value="20">20</option>
                                                            <option value="22">22</option>
                                                            <option value="24">24</option>
                                                            <option value="24">26</option>
                                                            <option value="24">28</option>
                                                            <option value="30">30</option>
                                                            <option value="32">32</option>
                                                            <option value="34">34</option>
                                                            <option value="36">36</option>
                                                        </select>
                                                    </div>
                                                    {/* Izquierda */}
                                                    <div className="mb-4">
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Izquierda:
                                                        </h4>
                                                        <input
                                                            type="number"
                                                            onChange={onChange}
                                                            id={`coord-left-${i}`}
                                                            name={`coord-left`}
                                                            value={
                                                                getValueWithoutUnit(
                                                                    coordStyles.current[i]?.left,
                                                                    'px',
                                                                ) ?? ''
                                                            }
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                                                        />
                                                    </div>
                                                    {/* Arriba */}
                                                    <div>
                                                        <h4 className="text-l text-gray-500 font-semibold mb-2">
                                                            Arriba:
                                                        </h4>
                                                        <input
                                                            type="number"
                                                            onChange={onChange}
                                                            id={`coord-top-${i}`}
                                                            name={`coord-top`}
                                                            value={
                                                                getValueWithoutUnit(
                                                                    coordStyles.current[i]?.top,
                                                                    'px',
                                                                ) ?? ''
                                                            }
                                                            className="mt-1 p-3 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring-3 focus:ring-indigo-200/50"
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    <div className="sticky bottom-6 flex justify-end mt-8">
                                        <button
                                            onClick={saveDesign}
                                            className="cursor-pointer rounded-full disabled:bg-gray-300 bg-emerald-400 hover:bg-emerald-500 text-white font-bold p-4 px-8 shadow-md z-50 transition-colors"
                                            title="Guardar Diseño"
                                            ref={saveButton}>
                                            Guardar Diseño
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </>
            ) : (
                <div className="container mx-auto mt-20 text-center">
                    <h1 className="text-3xl">Permission denied</h1>
                    <Link
                        to="/login"
                        className="relative block text-center mx-auto mt-8 py-4 px-8 bg-green-500 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-green-400/75">
                        Login
                    </Link>
                </div>
            )}
        </>
    );
};
export default Admin;
